/* eslint-disable no-nested-ternary */
import { Autocomplete, Divider, Grid, NumberInput, Select, Space, Stack, Text, TextInput } from '@mantine/core';
import { useContext } from 'react';
import { IconAlignLeft, IconBorderRadius, IconLetterCase, IconPhoto } from '@tabler/icons';
import { BookitupFormContext } from '../../../Service/Context/BookitupFormContext';
import MantineColorPicker from '../../../Atoms/inputs/MantineColorPicker';
import { WindowContext } from '../../../Service/Context/WindowContext';
import { DataS } from '../../../Service/DataS';
import GradientPicker from '../../../Atoms/inputs/GradientPicker';
import { ThemeText } from '../../../Atoms/ThemeText';

const ContactFormStyleOptions = () => {
  const { bookitupForm, bookitupFormStyles, updateFormStyles } = useContext(BookitupFormContext);
  const { xs, md } = useContext(WindowContext);
  const gridColSpanSize = xs ? 12 : md ? 6 : 4;

  if (!bookitupForm) {
    return null;
  }

  const {
    pageFormBackgroundColor,
    formBackgroundColor,
    backgroundImage,
    formTextColor,
    formLinkColor,
    fieldBackgroundColor,
    fieldTextColor,
    fieldLabelTextWeight,
    fieldBorderColor,
    fieldBorderFocusColor,
    submitButtonBackgroundColor,
    submitButtonHoverColor,
    submitButtonTextColor,
    fieldAsteriskColor,
    fieldBorderRadius,
    fieldBorderWidth,
    submitButtonBorderRadius,
    submitButtonPosition,
    fontFamily,
    formBorderRadius,
    formPadding,
  } = bookitupFormStyles;

  return (
    <>
      <Autocomplete
        data={[
          '"Roboto", sans-serif',
          '"Lato", sans-serif',
          '"Montserrat", sans-serif',
          '"Tenor Sans", sans-serif',
          '"Josefin Sans", sans-serif',
          '"Lora", serif',
          '"Akshar", sans-serif',
          '"Antic", sans-serif',
          '"Quicksand", sans-serif',
          '"Cormorant Garamond", serif',
        ]}
        label="Schriftart"
        defaultValue={fontFamily ?? undefined}
        onBlur={(e) => updateFormStyles({ fontFamily: e.currentTarget.value })}
        icon={<IconLetterCase size={20} />}
        limit={10}
        sx={{ maxWidth: 600 }}
      />
      <Text color="dimmed" size="xs" mt={5} sx={{ maxWidth: 600 }}>
        Du kannst hier entweder eine von uns vorgegebene Schriftart auswählen oder eine eigene Schriftart eintragen, die bereits auf deiner Webseite verwendet wird. Mehrere Schriftarten können mit Komma getrennt aufgelistet werden, falls eine Schriftart nicht geladen werden kann.
      </Text>
      <Divider my="xl" label={<ThemeText size="md">Mini-Webseite</ThemeText>} />
      <ThemeText mb="xl">
        Diese Einstellung ist nur für dich relevant, falls du z.B. keine Webseite hast und das Kontaktformular direkt in deiner Instagram-Bio verlinken möchtest oder falls du eine Webseite bei Jimdo hast, bei der keine Code-Einbettung möglich ist.
      </ThemeText>
      <MantineColorPicker
        label="Hintergrundfarbe"
        color={pageFormBackgroundColor ?? undefined}
        onChange={(val) => updateFormStyles({ pageFormBackgroundColor: val })}
      />
      <Divider my="xl" label={<ThemeText size="md">Formular</ThemeText>} />
      <TextInput
        mb="sm"
        label="Hintergrundbild"
        placeholder="http://domain.de/image-url.png"
        defaultValue={backgroundImage ?? undefined}
        onBlur={(e) => updateFormStyles({ backgroundImage: e.currentTarget.value })}
        icon={<IconPhoto size={20} />}
        sx={{ maxWidth: 600 }}
      />
      <NumberInput
        hideControls
        mb="sm"
        label="Rundung vom Formular"
        min={0}
        max={50}
        icon={<IconBorderRadius size={20} />}
        value={formBorderRadius ? Number(formBorderRadius) : 0}
        onBlur={(e) => e.currentTarget.value !== '' && updateFormStyles({ formBorderRadius: e.currentTarget.value })}
        rightSection={
          <ThemeText size="xs" weight="bolder">
            px
          </ThemeText>
        }
        sx={{ maxWidth: 600 }}
      />
      <NumberInput
        hideControls
        label="Abstand vom Formularrand zu den Eingabefeldern"
        min={0}
        max={50}
        icon={<IconBorderRadius size={20} />}
        value={formPadding ? Number(formPadding) : 10}
        onBlur={(e) => e.currentTarget.value !== '' && updateFormStyles({ formPadding: e.currentTarget.value })}
        rightSection={
          <ThemeText size="xs" weight="bolder">
            px
          </ThemeText>
        }
        sx={{ maxWidth: 600 }}
      />
      <Grid mt="md" sx={{ alignItems: 'flex-start' }}>
        <Grid.Col span={gridColSpanSize}>
          <GradientPicker
            initialGradient={formBackgroundColor}
            label="Hintergrundfarbe"
            onChange={(val) => updateFormStyles({ formBackgroundColor: val })}
          />
        </Grid.Col>
        <Grid.Col span={gridColSpanSize}>
          <MantineColorPicker
            label="Textfarbe"
            color={formTextColor}
            onChange={(val) => updateFormStyles({ formTextColor: val })}
          />
        </Grid.Col>
        <Grid.Col span={gridColSpanSize}>
          <GradientPicker
            initialGradient={submitButtonBackgroundColor}
            label="Absende-Button und Checkboxen"
            onChange={(val) => updateFormStyles({ submitButtonBackgroundColor: val })}
          />
        </Grid.Col>
        <Grid.Col span={gridColSpanSize}>
          <MantineColorPicker
            label="Linkfarbe"
            color={formLinkColor}
            onChange={(val) => updateFormStyles({ formLinkColor: val })}
          />
        </Grid.Col>
      </Grid>
      <Divider my="xl" label={<ThemeText size="md">Feld</ThemeText>} />

      <Autocomplete
        mb="sm"
        mt="xs"
        data={['lighter', 'normal', 'bold', 'bolder']}
        label="Schriftdicke der Feldbezeichnungen"
        defaultValue={fieldLabelTextWeight ?? 'bold'}
        onBlur={(e) => updateFormStyles({ fieldLabelTextWeight: e.currentTarget.value })}
        sx={{ maxWidth: 600 }}
      />

      <NumberInput
        hideControls
        label="Rundung der Eingabefelder"
        min={0}
        max={50}
        icon={<IconBorderRadius size={20} />}
        value={Number(fieldBorderRadius)}
        onBlur={(e) => e.currentTarget.value !== '' && updateFormStyles({ fieldBorderRadius: e.currentTarget.value })}
        rightSection={
          <ThemeText size="xs" weight="bolder">
            px
          </ThemeText>
        }
        sx={{ maxWidth: 600 }}
      />
      <Autocomplete
        mt="xs"
        data={['thin', 'medium', 'thick']}
        label="Randdicke der Eingabefelder"
        defaultValue={fieldBorderWidth ?? undefined}
        onBlur={(e) => updateFormStyles({ fieldBorderWidth: e.currentTarget.value })}
        sx={{ maxWidth: 600 }}
      />
      <Grid mt="md" sx={{ alignItems: 'flex-start' }}>
        <Grid.Col span={gridColSpanSize}>
          <GradientPicker
            initialGradient={fieldBackgroundColor}
            label="Hintergrundfarbe"
            onChange={(val) => updateFormStyles({ fieldBackgroundColor: val })}
          />
        </Grid.Col>
        <Grid.Col span={gridColSpanSize}>
          <MantineColorPicker
            label="Textfarbe"
            color={fieldTextColor}
            onChange={(val) => updateFormStyles({ fieldTextColor: val })}
          />
        </Grid.Col>
        <Grid.Col span={gridColSpanSize}>
          <MantineColorPicker
            label="Rahmenfarbe"
            color={fieldBorderColor}
            onChange={(val) => updateFormStyles({ fieldBorderColor: val })}
          />
        </Grid.Col>
        <Grid.Col span={gridColSpanSize}>
          <MantineColorPicker
            label="Rahmenfarbe bei Fokus"
            color={fieldBorderFocusColor}
            onChange={(val) => updateFormStyles({ fieldBorderFocusColor: val })}
          />
        </Grid.Col>
        <Grid.Col span={gridColSpanSize}>
          <MantineColorPicker
            label="Sternchenfarbe für Pflichtfelder"
            color={fieldAsteriskColor}
            onChange={(val) => updateFormStyles({ fieldAsteriskColor: val })}
          />
        </Grid.Col>
      </Grid>

      <Divider my="xl" label={<ThemeText size="md">Absendebutton</ThemeText>} />
      <Stack sx={{ maxWidth: 600 }}>
        <Select
          label="Ausrichtung"
          icon={<IconAlignLeft size={20} />}
          value={submitButtonPosition}
          onChange={(val) => val && updateFormStyles({ submitButtonPosition: val })}
          data={DataS.buttonPositionOptions}
        />

        <NumberInput
          hideControls
          label="Rundung vom Absendebutton"
          min={0}
          max={50}
          icon={<IconBorderRadius size={20} />}
          value={Number(submitButtonBorderRadius)}
          onBlur={(e) =>
            e.currentTarget.value !== '' && updateFormStyles({ submitButtonBorderRadius: e.currentTarget.value })
          }
          rightSection={
            <ThemeText size="xs" weight="bolder">
              px
            </ThemeText>
          }
        />
      </Stack>
      <Grid mt="md" sx={{ alignItems: 'flex-start' }}>
        <Grid.Col span={gridColSpanSize}>
          <MantineColorPicker
            label="Textfarbe"
            color={submitButtonTextColor}
            onChange={(val) => updateFormStyles({ submitButtonTextColor: val })}
          />
        </Grid.Col>
        <Grid.Col span={gridColSpanSize}>
          <MantineColorPicker
            label="Hoverfarbe"
            color={submitButtonHoverColor}
            onChange={(val) => updateFormStyles({ submitButtonHoverColor: val })}
          />
        </Grid.Col>
      </Grid>
      <Text color="dimmed" size="xs" mt="md" sx={{ maxWidth: 600 }}>
        Die Hintergrundfarbe vom Absendebutton kannst du oben im Bereich "Formular" mit der Farbe "Absende-Button und Checkboxen" einstellen.
      </Text>
    </>
  );
};

export default ContactFormStyleOptions;
